import { Alert, Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import { reloadCurrentUser, sendUserEmailVerification } from '../services/authService';

const VerifyEmailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshUserDetails } = useAccount();
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');
  const [lastSendTime, setLastSendTime] = useState(() => {
    const stored = sessionStorage.getItem('lastVerificationEmailTime');
    return stored ? Number.parseInt(stored, 10) : 0;
  });
  const [cooldown, setCooldown] = useState(0);
  const sendingRef = useRef(false);
  const hasAutoSent = useRef(false);

  const handleResendVerification = useCallback(async () => {
    if (!user) {
      return;
    }
    if (sendingRef.current) {
      return;
    }

    const now = Date.now();
    // Use 60000 (1 minute) consistently
    if (now - lastSendTime < 60000) {
      setSendError('You can only resend the verification email once per minute.');
      return;
    }

    sendingRef.current = true;
    setIsSending(true);
    setSendError('');
    setSendSuccess('');

    try {
      await sendUserEmailVerification();
      setSendSuccess('A new verification email has been sent. Please check your inbox.');
      const newTime = Date.now();
      setLastSendTime(newTime);
      sessionStorage.setItem('lastVerificationEmailTime', newTime.toString());
      setCooldown(60);
    } catch (error: unknown) {
      if ((error as { code?: string }).code === 'auth/too-many-requests') {
        setSendError('Verification email was sent recently. Please check your inbox.');
      } else {
        setSendError('Failed to send verification email. Please try again in a few minutes.');
      }
    } finally {
      setIsSending(false);
      sendingRef.current = false;
    }
  }, [user, lastSendTime]);

  useEffect(() => {
    if (user === null) {
      navigate('/login');
    }
  }, [user, navigate]);

  useEffect(() => {
    // Send verification email automatically when component mounts if not already sent
    if (user && !user.emailVerified) {
      // Check for E2E testing flag to prevent auto-send during tests
      if ((window as Window & { E2E_TESTING?: boolean }).E2E_TESTING) {
        return;
      }

      if (hasAutoSent.current) {
        return;
      }

      const now = Date.now();
      if (now - lastSendTime > 60000) {
        // 1 minute cooldown
        if (process.env.NODE_ENV !== 'test') {
          hasAutoSent.current = true;
          handleResendVerification();
        }
      }
    }
  }, [user, lastSendTime, handleResendVerification]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const interval = setInterval(async () => {
      const isVerified = await reloadCurrentUser();
      if (isVerified) {
        refreshUserDetails();
        navigate('/dashboard');
      }
    }, 3000); // Check every 3 seconds

    return () => clearInterval(interval);
  }, [user, refreshUserDetails, navigate]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [cooldown]);

  if (!user) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <img
            src="/Bidlogiq_Logo-Long_Full colours.png"
            alt="BIDLOGIQ Logo"
            style={{ maxWidth: '300px', marginBottom: '24px' }}
          />
          <Typography variant="h4" component="h1" gutterBottom>
            Verify Your Email
          </Typography>
          <Typography
            data-testid="verification-message"
            variant="body1"
            color="text.primary"
            sx={{ mb: 3 }}
          >
            A verification link has been sent to your email address: <strong>{user.email}</strong>.
            Please click the link to continue.
          </Typography>

          <Button
            onClick={handleResendVerification}
            disabled={isSending || cooldown > 0}
            variant="contained"
            sx={{ mb: 2 }}
          >
            {isSending ? (
              <CircularProgress size={24} />
            ) : cooldown > 0 ? (
              `Resend in ${cooldown}s`
            ) : (
              'Resend Verification Email'
            )}
          </Button>

          {sendSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {sendSuccess}
            </Alert>
          )}
          {sendError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {sendError}
            </Alert>
          )}

          <Typography variant="body2" sx={{ mt: 3 }}>
            Once your email is verified, you will be automatically redirected.
          </Typography>
          <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider', width: '100%' }}>
            <Typography variant="body2" color="text.primary">
              Having trouble? Contact support:
            </Typography>
            <Typography variant="body2" color="text.primary">
              Email: <a href="mailto:support@bidlogiq.ai">support@bidlogiq.ai</a>
            </Typography>
            <Typography variant="body2" color="text.primary">
              Phone: <a href="tel:+61721436035">+61 (0)7 2143 6035</a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyEmailPage;
